/**
 * Own-hero prediction for party runs (rewind + replay; docs/design/multiplayer.md §2.3).
 *
 * The confirmed sim (A) replays the room's frames, so it trails the room. To draw the own hero
 * where the player's input acts, this predictor keeps a second sim (P) holding only the party's
 * units. Every confirmed tick it
 *   1. rewinds: copies the party units (and the clock, doors, RNG) from A into P;
 *   2. replays: steps P forward to the present tick with the own commands the room has not acked
 *      yet, each applied on the room tick it will land on (net/netSession.ts estimates it);
 *   3. records the own hero's position after every replayed tick.
 * The renderer samples that path at the present (a fractional tick) and localMotion blends any
 * remaining error out over ~60 ms, snapping only for big jumps (respawn, teleport, knockback).
 *
 * P runs the sim's own code: movement and wall sliding (spatial.moveUnit), the dash effect,
 * canCast (charges, roots, stuns). Enemies are not in P, so only movement is predicted: moves,
 * stops, facing, and casts whose effects dash to a point or forward (Dodge). Everything else
 * (damage, knockbacks, enemy casts) arrives through A and is blended in.
 */
import type { Command, ContentRegistry, Sim, Unit, UnitId, Vec2, World } from '@keystone/sim';

/** A batch of own commands sent to the room, not yet acked. */
export interface PendingInput {
  seq: number;
  /** Room tick the batch is expected to be applied in. */
  landTick: number;
  cmds: Command[];
}

/** Longest prediction (ticks): a present further ahead than this means a bad link; stop there. */
export const MAX_PREDICT_TICKS = 20;
/** Ticks predicted past the present (the frame loop samples a little ahead while frames are late). */
const EXTRA_TICKS = 2;

export interface PredictedPath {
  /** A's tick: path[k] is the own position after tick + k. */
  tick: number;
  path: Vec2[];
  /** Commands the room had not acked at this update (diagnostics / tests). */
  unacked: number;
}

/** True when a command changes the own hero's movement (and P can run it without enemies). */
export function movesSelf(content: Pick<ContentRegistry, 'ability'>, c: Command): boolean {
  if (c.type === 'move' || c.type === 'stop' || c.type === 'face') return true;
  if (c.type === 'jump' || c.type === 'glide') return true; // +glide: hops and free flight are own movement too
  if (c.type !== 'cast') return false;
  const ab = content.ability(c.abilityId);
  return !!ab?.effects?.some((e) => e.type === 'dash' && e.mode !== 'toTarget');
}

export class OwnPredictor {
  readonly pending: PendingInput[] = [];
  acked = 0;
  last: PredictedPath | null = null;
  /** The own unit as a command sent now will find it when it lands (see `view`). */
  landing: Unit | null = null;
  /** Set when P failed once: prediction is off for the rest of the run (A is drawn as before). */
  broken = false;
  private stripped = false;

  /**
   * @param p A sim created with the same config as the confirmed sim (reduced to the party units
   *   on first use and stepped by nothing else).
   */
  constructor(
    private readonly p: Sim,
    readonly ownId: UnitId,
  ) {}

  /** Own commands just sent as batch `seq`, expected on room tick `landTick`. */
  record(seq: number, landTick: number, cmds: readonly Command[]): void {
    const own = cmds.filter((c) => c.actorId === this.ownId && movesSelf(this.p.content, c));
    if (own.length) this.pending.push({ seq, landTick, cmds: own });
  }

  /** The room applied every own batch up to `seq` (they are in A now). */
  ack(seq: number): void {
    if (seq <= this.acked) return;
    this.acked = seq;
    while (this.pending.length && this.pending[0]!.seq <= seq) this.pending.shift();
  }

  /**
   * Rewind to A and replay the unacked inputs up to `horizonTick` (fractional; the present plus a
   * little). `landNowTick`: the tick a command sent now lands on (for `view`). Null when off.
   */
  update(a: Readonly<World>, landNowTick: number, horizonTick: number): PredictedPath | null {
    if (this.broken) return null;
    // An ack lost with a skipped frame (snapshot resync): long past its tick, it landed.
    while (this.pending.length && this.pending[0]!.landTick < a.tick - 40) this.pending.shift();
    try {
      const w = this.rewind(a);
      const steps = Math.max(1, Math.min(MAX_PREDICT_TICKS, Math.ceil(horizonTick - a.tick) + EXTRA_TICKS));
      // A batch landing on tick L is applied by the step that produces L (step index L - A - 1).
      const byStep = new Map<number, Command[]>();
      for (const inp of this.pending) {
        const k = Math.max(0, Math.min(steps - 1, inp.landTick - a.tick - 1));
        byStep.set(k, [...(byStep.get(k) ?? []), ...inp.cmds]);
      }
      const landIdx = Math.max(0, Math.min(steps, landNowTick - 1 - a.tick));
      const path: Vec2[] = [{ ...w.units[this.ownId]!.pos }];
      this.landing = landIdx === 0 ? structuredClone(w.units[this.ownId]!) : null;
      for (let k = 0; k < steps; k++) {
        this.p.step(byStep.get(k) ?? []);
        const u = this.p.getWorld().units[this.ownId];
        if (!u) break;
        path.push({ ...u.pos });
        if (k + 1 === landIdx) this.landing = structuredClone(u);
      }
      this.last = { tick: a.tick, path, unacked: this.pending.length };
      return this.last;
    } catch (err) {
      console.warn('[net] own-hero prediction failed; drawing the confirmed state', err);
      this.broken = true;
      this.last = null;
      this.landing = null;
      return null;
    }
  }

  /**
   * A's world with the own unit as a command sent now will find it when it lands: what the player
   * controller compares the held keys against (a move "lost" to a dash is resent so it lands as
   * the dash ends, not a round trip later). A itself is not modified.
   */
  view(a: Readonly<World>): World {
    if (!this.landing || this.broken) return a as World;
    return { ...(a as World), units: { ...a.units, [this.ownId]: this.landing } };
  }

  /** The own position at room tick `tick` (fractional) from the last update, or null. */
  sample(tick: number): Vec2 | null {
    const l = this.last;
    if (!l || l.path.length === 0) return null;
    const f = Math.max(0, Math.min(l.path.length - 1, tick - l.tick));
    const i = Math.floor(f);
    const a = l.path[i]!;
    const b = l.path[Math.min(l.path.length - 1, i + 1)]!;
    const t = f - i;
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }

  /** P := A for the party's units, clock, doors and RNG (enemies are left out of P). */
  private rewind(a: Readonly<World>): World {
    const w = this.p.getWorld() as World;
    if (!this.stripped) {
      this.stripped = true;
      w.unitOrder = [...a.partyIds];
      w.units = {};
      w.packs = {};
      w.encounter = null;
      w.mechanics = undefined;
      w.worldObjects = [];
      w.pickups = [];
    }
    w.tick = a.tick;
    w.time = a.time;
    w.rngState = structuredClone(a.rngState);
    w.nextSeq = a.nextSeq;
    w.mplus = structuredClone(a.mplus);
    w.doors = a.doors ? structuredClone(a.doors) : undefined;
    w.telegraphs = [];
    w.zones = [];
    w.events = [];
    for (const id of a.partyIds) {
      const u = a.units[id];
      if (u) w.units[id] = structuredClone(u);
    }
    return w;
  }
}
